// src/pages/Products/Products.js
import React, {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
import {productService} from '../../services/api';
import {useAuth} from '../../contexts/AuthContext';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronUpIcon,
} from '@heroicons/react/24/outline';
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal';

const Products = () => {
    const { hasAnyRole } = useAuth();
    const [products, setProducts] = useState([]);
    const [baseProducts, setBaseProducts] = useState([]);
    const [expandedProducts, setExpandedProducts] = useState({});
    const [areAllExpanded, setAreAllExpanded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
                                                     key: 'sku',
                                                     direction: 'asc'
                                                 });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);

    // Fetch products
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await productService.getProductsAndVariants();

                setProducts(response.data);
                setBaseProducts(response.base_products || []);

                // Initialize expanded state for all products to false (collapsed)
                const initialExpandedState = {};
                (response.base_products || []).forEach(product => {
                    initialExpandedState[product.id] = false;
                });
                setExpandedProducts(initialExpandedState);

                setError(null);
            }
            catch (err) {
                console.error('Error fetching products:', err);
                setError('Failed to load products. Please try again later.');
                toast.error('Failed to load products');
            }
            finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Toggle product expansion
    const toggleProductExpansion = (productId, event) => {
        if (event) {
            event.stopPropagation();
        }
        setExpandedProducts(prevState => ({
            ...prevState,
            [productId]: !prevState[productId]
        }));
    };

    // Toggle all products expansion
    const toggleAllExpansion = () => {
        const newExpandedState = {};
        baseProducts.forEach(product => {
            newExpandedState[product.id] = !areAllExpanded;
        });
        setExpandedProducts(newExpandedState);
        setAreAllExpanded(!areAllExpanded);
    };

    // Sort products
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sorted base products
    const getSortedBaseProducts = () => {
        if (!baseProducts || baseProducts.length === 0) return [];

        return [...baseProducts].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Handle nested properties
            if (sortConfig.key.includes('.')) {
                const keys = sortConfig.key.split('.');
                aValue = a;
                bValue = b;

                for (const key of keys) {
                    aValue = aValue?.[key];
                    bValue = bValue?.[key];
                }
            }

            // Handle null or undefined values
            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            // String comparison
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortConfig.direction === 'asc'
                       ? aValue.localeCompare(bValue)
                       : bValue.localeCompare(aValue);
            }

            // Numeric comparison
            return sortConfig.direction === 'asc'
                   ? (aValue > bValue ? 1 : -1)
                   : (aValue < bValue ? 1 : -1);
        });
    };

    // Get variants for a specific base product
    const getVariantsForProduct = (productId) => {
        return products.filter(p => p.parent_id === productId && p.is_variant);
    };

    // Filter products by search term
    const getFilteredBaseProducts = () => {
        if (!searchTerm) return getSortedBaseProducts();

        const filteredBaseProducts = [];
        const baseProductIds = new Set();

        // First, check if any base product matches
        const matchingBaseProducts = getSortedBaseProducts().filter(product =>
                                                                        [product.sku, product.name, product.display_name, product.description]
                                                                            .filter(Boolean)
                                                                            .some(field => field.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        matchingBaseProducts.forEach(product => {
            baseProductIds.add(product.id);
            filteredBaseProducts.push(product);
        });

        // Then check if any variant matches, and if so include its base product
        products.forEach(product => {
            if (product.is_variant && product.parent_id && !baseProductIds.has(product.parent_id)) {
                const matches = [product.sku, product.name, product.display_name, product.description]
                    .filter(Boolean)
                    .some(field => field.toLowerCase().includes(searchTerm.toLowerCase()));

                if (matches) {
                    // Find and add the base product
                    const baseProduct = baseProducts.find(bp => bp.id === product.parent_id);
                    if (baseProduct) {
                        baseProductIds.add(baseProduct.id);
                        filteredBaseProducts.push(baseProduct);
                    }
                }
            }
        });

        return filteredBaseProducts;
    };

    // Delete product handler
    const handleDeleteClick = (product, event) => {
        if (event) {
            event.stopPropagation();
        }
        setProductToDelete(product);
        setDeleteModalOpen(true);
    };

    // Confirm delete product
    const confirmDelete = async () => {
        if (!productToDelete) return;

        try {
            if (productToDelete.variant) {
                // Delete just the variant
                await productService.deleteProductVariant(productToDelete.id, productToDelete.variant);

                // Update products state to remove the variant
                setProducts(prevProducts =>
                                prevProducts.filter(p => !(p.id === productToDelete.id && p.variant === productToDelete.variant))
                );

                toast.success(`Product variant ${productToDelete.display_name} deleted successfully`);
            } else {
                // Delete the entire product
                await productService.deleteProduct(productToDelete.id);

                // Update products state to remove the product and its variants
                setProducts(prevProducts =>
                                prevProducts.filter(p => p.id !== productToDelete.id && p.parent_id !== productToDelete.id)
                );

                // Update base products state
                setBaseProducts(prevBaseProducts =>
                                    prevBaseProducts.filter(p => p.id !== productToDelete.id)
                );

                toast.success(`Product ${productToDelete.sku} deleted successfully`);
            }
        }
        catch (error) {
            console.error('Error deleting product:', error);
            toast.error(error.response?.data?.message || 'Failed to delete product');
        }
        finally {
            setDeleteModalOpen(false);
            setProductToDelete(null);
        }
    };

    // Determine warning levels for stock
    const getStockClass = (stock, required = 1) => {
        if (stock === 0) return 'text-red-600 font-bold';
        if (stock < required) return 'text-yellow-600 font-bold';
        return 'text-green-600';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-gray-800">Products</h1>

                {hasAnyRole(['admin', 'procurement']) && (
                    <Link
                        to="/products/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <PlusIcon className="h-5 w-5 mr-2"/> Add Product
                    </Link>
                )}
            </div>

            {/* Search and filter */}
            <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center space-x-4">
                    <div className="w-full max-w-xs">
                        <label htmlFor="search" className="sr-only">
                            Search
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400"/>
                            </div>
                            <input
                                id="search"
                                name="search"
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                placeholder="Search products by SKU or name"
                            />
                        </div>
                    </div>

                    {/* Expand/Collapse All Toggle Button */}
                    <button
                        onClick={toggleAllExpansion}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        {areAllExpanded ? (
                            <>
                                <ChevronUpIcon className="h-5 w-5 mr-2"/> Collapse All
                            </>
                        ) : (
                             <>
                                 <ChevronDownIcon className="h-5 w-5 mr-2"/> Expand All
                             </>
                         )}
                    </button>
                </div>
            </div>

            {/* Products List */}
            <div className="bg-white shadow rounded-lg overflow-hidden flex flex-col max-h-[calc(100vh-12rem)]">
                <div className="overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                        <tr>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('sku')}
                            >
                                <div className="flex items-center">
                                    SKU
                                    {sortConfig.key === 'sku' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('display_name')}
                            >
                                <div className="flex items-center">
                                    Name
                                    {sortConfig.key === 'display_name' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('foam_stock')}
                            >
                                <div className="flex items-center">
                                    Foam
                                    {sortConfig.key === 'foam_stock' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('front_cover_stock')}
                            >
                                <div className="flex items-center">
                                    Front Cover
                                    {sortConfig.key === 'front_cover_stock' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('back_cover_stock')}
                            >
                                <div className="flex items-center">
                                    Back Cover
                                    {sortConfig.key === 'back_cover_stock' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('pending_orders')}
                            >
                                <div className="flex items-center">
                                    Pending Orders
                                    {sortConfig.key === 'pending_orders' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('fulfillable_percentage')}
                            >
                                <div className="flex items-center">
                                    Fulfillment %
                                    {sortConfig.key === 'fulfillable_percentage' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                onClick={() => requestSort('fulfillable_count')}

                            >
                                Fulfillable
                                {sortConfig.key === 'fulfillable_count' && (
                                    <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {getFilteredBaseProducts().length > 0 ? (
                            getFilteredBaseProducts().flatMap((baseProduct, index) => {
                                const rows = [];
                                const variants = getVariantsForProduct(baseProduct.id);
                                const hasVariants = variants.length > 0;

                                // Use alternating background colors
                                const isEven = index % 2 === 0;
                                const bgColorClass = isEven ? 'bg-white' : 'bg-gray-100';

                                // Add base product row
                                rows.push(
                                    <tr
                                        key={`base-${baseProduct.id}`}
                                        className={`hover:bg-gray-100 ${bgColorClass} cursor-pointer`}
                                        onClick={() => toggleProductExpansion(baseProduct.id)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600">
                                            <div className="flex items-center">
                                                <Link
                                                    to={`/products/${baseProduct.id}`}
                                                    className="hover:underline font-bold"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {baseProduct.sku}
                                                </Link>
                                                {baseProduct.has_variants && variants.length > 0 && (
                                                    <button
                                                        onClick={(e) => toggleProductExpansion(baseProduct.id, e)}
                                                        className="ml-2 focus:outline-none"
                                                    >
                                                        {expandedProducts[baseProduct.id] ? (
                                                            <ChevronDownIcon className="h-4 w-4 text-gray-500"/>
                                                        ) : (
                                                             <ChevronRightIcon className="h-4 w-4 text-gray-500"/>
                                                         )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {baseProduct.display_name || baseProduct.name}
                                        </td>
                                        {/* Show stock levels only for products without variants */}
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(baseProduct.foam_stock, baseProduct.pending_orders)}`}>
                                            {baseProduct.foam_stock}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(baseProduct.front_cover_stock, baseProduct.pending_orders)}`}>
                                            {baseProduct.front_cover_stock}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(baseProduct.back_cover_stock, baseProduct.pending_orders)}`}>
                                            {baseProduct.back_cover_stock}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(baseProduct.pending_orders)}`}>
                                            {baseProduct.pending_orders}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className={`${baseProduct.fulfillable_percentage < 50 ? 'text-red-600 font-bold' : baseProduct.fulfillable_percentage < 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                                                {baseProduct.fulfillable_percentage}%
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className="text-green-600">{baseProduct.fulfillable_count}</span> /
                                            <span className="text-red-600"> {baseProduct.unfulfillable_count}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-2">
                                                <Link
                                                    to={`/products/${baseProduct.id}`}
                                                    className="text-primary-600 hover:text-primary-900"
                                                    title="View"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <EyeIcon className="h-5 w-5"/>
                                                </Link>

                                                {hasAnyRole(['admin', 'procurement']) && (
                                                    <>
                                                        <Link
                                                            to={`/products/${baseProduct.id}/edit`}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                            title="Edit"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <PencilIcon className="h-5 w-5"/>
                                                        </Link>

                                                        <button
                                                            onClick={(e) => handleDeleteClick(baseProduct, e)}
                                                            className="text-red-600 hover:text-red-900"
                                                            title="Delete Product"
                                                        >
                                                            <TrashIcon className="h-5 w-5"/>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );

                                // Add variant rows if expanded
                                if (expandedProducts[baseProduct.id] && variants.length > 0) {
                                    variants.forEach(variant => {
                                        rows.push(
                                            <tr
                                                key={`variant-${baseProduct.id}-${variant.variant}`}
                                                className={`hover:bg-gray-100 ${bgColorClass}`}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600 pl-12 border-t border-gray-100">
                                                    <Link to={`/products/${variant.id}`} className="hover:underline">
                                                        {variant.sku}
                                                    </Link>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-t border-gray-100">
                                                    {variant.display_name}
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(variant.foam_stock, variant.pending_orders)} border-t border-gray-100`}>
                                                    {variant.foam_stock}
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(variant.front_cover_stock, variant.pending_orders)} border-t border-gray-100`}>
                                                    {variant.front_cover_stock}
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(variant.back_cover_stock, variant.pending_orders)} border-t border-gray-100`}>
                                                    {variant.back_cover_stock}
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${getStockClass(variant.pending_orders)} border-t border-gray-100`}>
                                                    {variant.pending_orders}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm border-t border-gray-100">
                                                    <div className={`${variant.fulfillable_percentage < 50 ? 'text-red-600 font-bold' : variant.fulfillable_percentage < 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                                                        {variant.fulfillable_percentage}%
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-t border-gray-100">
                                                    <span className="text-green-600">{variant.fulfillable_count}</span> /
                                                    <span className="text-red-600"> {variant.unfulfillable_count}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border-t border-gray-100">
                                                    <div className="flex justify-end space-x-2">
                                                        <Link
                                                            to={`/products/${variant.id}`}
                                                            className="text-primary-600 hover:text-primary-900"
                                                            title="View"
                                                        >
                                                            <EyeIcon className="h-5 w-5"/>
                                                        </Link>

                                                        {hasAnyRole(['admin', 'procurement']) && (
                                                            <>
                                                                <Link
                                                                    to={`/products/${variant.id}/edit`}
                                                                    className="text-indigo-600 hover:text-indigo-900"
                                                                    title="Edit"
                                                                >
                                                                    <PencilIcon className="h-5 w-5"/>
                                                                </Link>

                                                                <button
                                                                    onClick={(e) => handleDeleteClick(variant, e)}
                                                                    className="text-red-600 hover:text-red-900"
                                                                    title="Delete Variant"
                                                                >
                                                                    <TrashIcon className="h-5 w-5"/>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    });
                                }

                                return rows;
                            })
                        ) : (
                             <tr>
                                 <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                                     {searchTerm ? 'No matching products found.' : 'No products found.'}
                                 </td>
                             </tr>
                         )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title={productToDelete?.variant ? "Delete Product Variant" : "Delete Product"}
                message={productToDelete?.variant
                         ? `Are you sure you want to delete the ${productToDelete.variant} variant of ${productToDelete.name}? This action cannot be undone.`
                         : `Are you sure you want to delete ${productToDelete?.sku}? This will remove all variants of this product. This action cannot be undone.`}
            />
        </div>
    );
};

export default Products;
